import {TradingAccount_TradingAccountParameter, TradingAccount } from '../proto/generated/trading-bridge'
import { getBrokerLoginUrl } from '../services/apis/tradingAccount'
import { getRedirectUrl } from './authentication'
import { Params } from '../proto/generated/common'
import { convertMapToKeyValueArray } from './common'

export enum BrokerId {
    ANGEL_ONE = 'ANGEL_ONE',
    UPSTOX = 'UPSTOX',   
    FYERS = 'FYERS',
    PAPER_TRADE = 'PAPER_TRADE',
    /** @deprecated Use PAPER_TRADE; kept for legacy accounts */
    PAPER_TRADING = 'PAPER_TRADING',
    BESSEGGEN = 'BESSEGGEN',
    ZERODHA = 'ZERODHA',
    BROKER_UNKNOWN = 'BROKER_UNKNOWN',
    DHAN = 'DHAN',
    UNRECOGNIZED = 'UNRECOGNIZED'
}

export const isPaperBrokerCode = (brokerCode: string): boolean =>
    brokerCode === BrokerId.PAPER_TRADE || brokerCode === BrokerId.PAPER_TRADING

export const isBesseggenBrokerCode = (brokerCode: string): boolean =>
    brokerCode === BrokerId.BESSEGGEN

export const isPracticeTradeAccountEnabled = (status?: number): boolean =>
    status === 1 // TradingAccountStatus.ACTIVE

export const isPracticeTradeAccountDisabled = (status?: number): boolean =>
    status === 12 // TradingAccountStatus.DISABLED

export const isRealTradingAccount = (brokerCode: string): boolean =>
    !isPaperBrokerCode(brokerCode)

export const getTradingAccountUccCode = (
    account: TradingAccount,
    profileClientCode?: string
): string | undefined => {
    const fromAccount = account.clientCode?.trim()
    if (fromAccount) {
        return fromAccount
    }

    const fromProfile = profileClientCode?.trim()
    if (fromProfile) {
        return fromProfile
    }

    const params = jsonObjFromTradingAccountParams(account.tradingAccountParameters ?? [])
    const fromParams = params.ucc ?? params.UCC ?? params.client_code ?? params.clientCode
    if (typeof fromParams === 'string' && fromParams.trim()) {
        return fromParams.trim()
    }

    return undefined
}

export const getTradingAccountSelectorLabel = (
    account: TradingAccount,
    profileClientCode?: string
): string => {
    if (isPaperBrokerCode(account.brokerCode)) {
        return 'Practice Trade'
    }
    return getTradingAccountUccCode(account, profileClientCode) ?? '—'
}

export const getBrokerId = (brokerCode: string): BrokerId => {
    switch (brokerCode) {
        case 'ANGEL_ONE':
            return BrokerId.ANGEL_ONE
        case 'UPSTOX':
            return BrokerId.UPSTOX
        case 'FYERS':
            return BrokerId.FYERS
        case 'PAPER_TRADE':
            return BrokerId.PAPER_TRADE
        case 'PAPER_TRADING':
            return BrokerId.PAPER_TRADE
        case 'BESSEGGEN':
            return BrokerId.BESSEGGEN
        case 'ZERODHA':
            return BrokerId.ZERODHA
        case 'DHAN':
            return BrokerId.DHAN
        default:
            return BrokerId.BROKER_UNKNOWN
    }
}

export const getBrokerImageType = (brokerCode: string): 'img' | 'svg' => {
    if (isPaperBrokerCode(brokerCode) || isBesseggenBrokerCode(brokerCode)) {
        return 'svg'
    }
    return 'img'
}

export const getBrokerAddFundsUrl = (brokerCode: string): any => {
    switch (brokerCode) {
        case 'ANGEL_ONE':
            return 'https://www.angelone.in/trade/account/add-funds'
        case 'UPSTOX':
            return 'https://pro.upstox.com/funds/securities/wallet'
        case 'FYERS':
            return 'https://fundtransfer.fyers.in/v2/'
        case 'ZERODHA':
            return 'https://kite.zerodha.com/funds'
        case 'DHAN':
            return 'https://web.dhan.co/index/money'
        default:
            return ''
    }
}

export const jsonObjFromTradingAccountParams = (params: TradingAccount_TradingAccountParameter[]) => {
    const obj: any = {}
    params.forEach((param) => {
        obj[param.parameterName] = param.parameterValue
    })
    return obj
}

export const keyValueArrayFromTradingAccountParams = (params: TradingAccount_TradingAccountParameter[]) => {
    const obj: Params[] = []
    params.forEach((param) => {
        obj.push({
            key: param.parameterName,
            value: param.parameterValue
        })
    })
    return obj
}

export const generateBrokerLoginUrl = async (brokerCode: string, params: Params[]): Promise<string> => {
    const url = await getBrokerLoginUrl(
        {
            body: {
                brokerCode: brokerCode,
                params: [ ...params, ...((brokerCode === BrokerId.UPSTOX) ? convertMapToKeyValueArray({ 'redirectUri': getRedirectUrl('UPSTOX') }) : []) ]
            }
        }
    )
    if (url) {
        return url.loginUrl
    } else {
        return ''
    }
}